import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ArrowLeft, Loader2, Save } from "lucide-react";
import { formatDateTime } from "../lib/utils";
import {
  fetchUserById,
  updateUserRole,
  updateUserStatus,
  clearUserDetail,
} from "../redux/slices/userSlice";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { useToast } from "../components/ui/use-toast";

export default function UserDetails() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { toast } = useToast();
  const authUser = useSelector((state) => state.auth.user);
  const { detail, detailLoading } = useSelector((state) => state.users);
  const isSuperAdmin = authUser?.role === "super_admin";

  const [role, setRole] = useState("");
  const [status, setStatus] = useState("");
  const [savingRole, setSavingRole] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);

  useEffect(() => {
    if (id) dispatch(fetchUserById(id));
    return () => dispatch(clearUserDetail());
  }, [dispatch, id]);

  useEffect(() => {
    if (!detail) return;
    setRole(detail.role);
    setStatus(detail.status);
  }, [detail]);

  const saveRole = async () => {
    if (!id || !detail || role === detail.role) return;
    setSavingRole(true);
    try {
      await dispatch(updateUserRole({ id, role })).unwrap();
      toast({ title: "Role updated" });
      dispatch(fetchUserById(id));
    } catch (err) {
      toast({
        title: "Error",
        description: typeof err === "string" ? err : "Could not update role",
        variant: "destructive",
      });
      setRole(detail.role);
    } finally {
      setSavingRole(false);
    }
  };

  const saveStatus = async () => {
    if (!id || !detail || status === detail.status) return;
    const adminStatuses = ["active", "inactive", "suspended"];
    if (!adminStatuses.includes(status)) {
      toast({
        title: "Not supported",
        description:
          "Status can only be set to active, inactive, or suspended from the admin panel.",
        variant: "destructive",
      });
      setStatus(detail.status);
      return;
    }
    setSavingStatus(true);
    try {
      await dispatch(updateUserStatus({ id, status })).unwrap();
      toast({ title: "Status updated" });
      dispatch(fetchUserById(id));
    } catch (err) {
      toast({
        title: "Error",
        description: typeof err === "string" ? err : "Could not update status",
        variant: "destructive",
      });
      setStatus(detail.status);
    } finally {
      setSavingStatus(false);
    }
  };

  if (detailLoading && !detail) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" asChild>
          <Link to="/users">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Users
          </Link>
        </Button>
        <p className="text-muted-foreground">User not found.</p>
      </div>
    );
  }

  const profiles = detail.profiles || [];
  const isSelf = authUser?.id === detail.id;

  return (
    <div className="space-y-6">
      <Button variant="ghost" asChild>
        <Link to="/users">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Users
        </Link>
      </Button>

      <div>
        <h1 className="text-3xl font-bold">{detail.email}</h1>
        <p className="text-muted-foreground font-mono text-sm">{detail.id}</p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Account</CardTitle>
            <CardDescription>Role and lifecycle</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline" className="capitalize">
                Auth: {detail.auth_provider}
              </Badge>
              <Badge variant="secondary" className="capitalize">
                Email {detail.email_verified ? "verified" : "unverified"}
              </Badge>
            </div>
            <div className="grid gap-2">
              <span className="text-sm font-medium">Role</span>
              <div className="flex flex-wrap gap-2 items-center">
                <Select
                  value={role}
                  onValueChange={setRole}
                  disabled={isSelf}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">User</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    {isSuperAdmin && (
                      <SelectItem value="super_admin">Super admin</SelectItem>
                    )}
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  onClick={saveRole}
                  disabled={isSelf || savingRole || role === detail.role}
                >
                  {savingRole ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-1" />
                  )}
                  Save role
                </Button>
              </div>
              {isSelf && (
                <p className="text-xs text-muted-foreground">
                  You cannot change your own role from here.
                </p>
              )}
            </div>
            <div className="grid gap-2">
              <span className="text-sm font-medium">Status</span>
              <div className="flex flex-wrap gap-2 items-center">
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="w-[220px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                    <SelectItem value="pending_verification">
                      Pending verification
                    </SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  onClick={saveStatus}
                  disabled={savingStatus || status === detail.status}
                >
                  {savingStatus ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-1" />
                  )}
                  Save status
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Current:{" "}
                <span className="capitalize">
                  {detail.status?.replace("_", " ")}
                </span>
              </p>
            </div>
            <div className="text-sm text-muted-foreground space-y-1">
              <p>
                Max profiles: <strong>{detail.max_profiles}</strong>
              </p>
              <p>
                Last login:{" "}
                {detail.last_login_at
                  ? formatDateTime(detail.last_login_at)
                  : "—"}
              </p>
              <p>
                Created:{" "}
                {detail.created_at || detail.createdAt
                  ? formatDateTime(detail.created_at || detail.createdAt)
                  : "—"}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Profiles</CardTitle>
            <CardDescription>Learning profiles for this account</CardDescription>
          </CardHeader>
          <CardContent>
            {profiles.length === 0 ? (
              <p className="text-sm text-muted-foreground">No profiles yet.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Primary</TableHead>
                    <TableHead>Language</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {profiles.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell>
                        {p.full_name || p.profile_name || "—"}
                      </TableCell>
                      <TableCell>{p.is_primary ? "Yes" : "—"}</TableCell>
                      <TableCell>{p.preferred_language || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
